import { LoadScheduler } from './load-scheduler.js';

/**
 * Shared high-resolution image sampler.
 *
 * Very large card and portrait sources can alias when a browser reduces them
 * to a small live element in one step. This utility performs a bounded series
 * of high-quality half-size reductions, then swaps in the sampled raster only
 * while the original request is still authoritative. The direct source stays
 * visible on every unsupported, stale, or failed path.
 */
(function (root, factory) {
  var api = factory(root || {});
  if (root && !root.MoonfallRasterSampler) root.MoonfallRasterSampler = api;
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  /* Above this reduction the engines' one-shot filter stops behaving like a
     box filter and starts aliasing; below it, stepping only costs detail. */
  var SINGLE_DRAW_MAX_RATIO = 8;

  var DEFAULT_CACHE_LIMIT = 64;
  var DEFAULT_CACHE_BYTE_LIMIT = 64 * 1024 * 1024;
  var DEFAULT_DECODED_SOURCE_LIMIT = 16;
  var DEFAULT_DECODED_PIXEL_LIMIT = 24 * 1024 * 1024;
  var DEFAULT_LOAD_TIMEOUT_MS = 3000;
  var DEFAULT_OVERSAMPLE = 2;

  function finitePositive(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function clampPosition(value, fallback) {
    if (typeof value === 'string') {
      var match = value.trim().match(/^(-?\d+(?:\.\d+)?)%$/);
      if (match) value = Number(match[1]) / 100;
    }
    var number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.max(0, Math.min(1, number));
  }

  function computeRasterPlan(options) {
    var input = options || {};
    var sourceWidth = finitePositive(input.sourceWidth, 0);
    var sourceHeight = finitePositive(input.sourceHeight, 0);
    var displayWidth = finitePositive(input.displayWidth, 0);
    var displayHeight = finitePositive(input.displayHeight, 0);
    if (!sourceWidth || !sourceHeight || !displayWidth || !displayHeight) {
      throw new TypeError('Raster dimensions must be finite positive numbers');
    }

    var dpr = finitePositive(input.dpr, 1);
    var oversample = finitePositive(input.oversample, DEFAULT_OVERSAMPLE);
    var desiredWidth = Math.max(1, Math.ceil(displayWidth * dpr * oversample));
    var desiredHeight = Math.max(1, Math.ceil(displayHeight * dpr * oversample));
    var fit = input.fit === 'cover' ? 'cover' : 'contain';
    var positionX = clampPosition(input.positionX, 0.5);
    var positionY = clampPosition(input.positionY, 0.5);
    var source = { x: 0, y: 0, width: sourceWidth, height: sourceHeight };

    if (fit === 'cover') {
      var targetAspect = desiredWidth / desiredHeight;
      var sourceAspect = sourceWidth / sourceHeight;
      if (sourceAspect > targetAspect) {
        source.width = sourceHeight * targetAspect;
        source.x = (sourceWidth - source.width) * positionX;
      } else if (sourceAspect < targetAspect) {
        source.height = sourceWidth / targetAspect;
        source.y = (sourceHeight - source.height) * positionY;
      }
    }

    var scale = Math.min(
      desiredWidth / source.width,
      desiredHeight / source.height,
      1,
    );
    var targetWidth = Math.max(1, Math.round(source.width * scale));
    var targetHeight = Math.max(1, Math.round(source.height * scale));

    return {
      fit: fit,
      source: source,
      targetWidth: targetWidth,
      targetHeight: targetHeight,
      direct: scale >= 1,
    };
  }

  function normaliseRequest(request, getDpr) {
    var input = request || {};
    return {
      displayWidth: finitePositive(input.displayWidth, 0),
      displayHeight: finitePositive(input.displayHeight, 0),
      fit: input.fit === 'cover' ? 'cover' : 'contain',
      positionX: clampPosition(input.positionX, 0.5),
      positionY: clampPosition(input.positionY, 0.5),
      dpr: finitePositive(input.dpr, finitePositive(getDpr(), 1)),
      oversample: finitePositive(input.oversample, DEFAULT_OVERSAMPLE),
    };
  }

  function cacheNumber(value) {
    return Math.round(value * 1000) / 1000;
  }

  function requestKey(url, request) {
    return [
      url,
      cacheNumber(request.displayWidth),
      cacheNumber(request.displayHeight),
      request.fit,
      cacheNumber(request.positionX),
      cacheNumber(request.positionY),
      cacheNumber(request.dpr),
      cacheNumber(request.oversample),
    ].join('|');
  }

  function defaultCanvas(width, height) {
    if (!root.document || typeof root.document.createElement !== 'function') return null;
    var canvas = root.document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  function configureContext(canvas) {
    if (!canvas || typeof canvas.getContext !== 'function') {
      throw new Error('Canvas sampling is unavailable');
    }
    var context = canvas.getContext('2d');
    if (!context) throw new Error('2D canvas sampling is unavailable');
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    return context;
  }

  /**
   * Does this engine actually honour `imageSmoothingQuality = 'high'`?
   *
   * A probe, never a UA sniff: engines that do not support it silently keep
   * their previous value. Where it IS honoured the engine performs a proper
   * high-quality downsample in a single draw, and the manual stepped halving
   * below becomes actively harmful — every half-step is another low-pass, and
   * three of them stacked on top of a filter that was already good throws away
   * detail the card never gets back.
   *
   * Measured on the real 1280x1589 card still reduced to 225x282:
   *   WebKit    stepped 9267  vs  direct 16130   (43% of the detail lost)
   *   Chromium  stepped 3498  vs  direct  4731   (26% lost)
   *
   * That gap IS the reported "normal cards in the collection are blurry but
   * stellar ones are sharp": a Stellar card paints through a <video> and never
   * enters this path, so the same artwork came out visibly softer in its
   * normal variant. It is worse on WebKit, which is why it surfaced on a phone
   * while Chrome and Firefox runs looked acceptable.
   */
  function honoursHighQualitySmoothing(context) {
    try {
      if (!context) return false;
      /* Assign and read back — nothing else. An `'imageSmoothingQuality' in
         context` guard looks like the careful version and is wrong: Firefox
         reports false for it (the property is not present until assigned) yet
         plainly acts on the value, so the guard sent the engine that loses the
         MOST detail (3.08x) down the very path that loses it. */
      context.imageSmoothingQuality = 'high';
      return context.imageSmoothingQuality === 'high';
    } catch (_) {
      return false;
    }
  }

  /**
   * Does this engine downscale better through `createImageBitmap`'s
   * `resizeQuality: 'high'` than through canvas-2D `drawImage`?
   *
   * Measured on the real 1280x1589 card still reduced to a 127x157 collection
   * tile — Laplacian variance, higher is sharper:
   *
   *              canvas-2D    createImageBitmap
   *   Chromium      3019            12443     +312%   <-- 4.12x
   *   WebKit       13398            12480      -7%    neutral
   *   Firefox      16143             5562      -66%   <-- HARMFUL
   *
   * Chromium's canvas-2D reduction is simply poor — roughly a fifth of what the
   * other two engines extract from identical input at identical geometry — and
   * `createImageBitmap` recovers almost all of it. Firefox stays off it: its
   * bitmap reduction is clean and sharp but runs on the main thread, 70-80 ms
   * per card on a desktop CPU against 1-5 ms for canvas-2D (27 October 2026).
   * WebKit gains nothing, so this is never applied everywhere.
   *
   * Measured against an exact Lanczos3 reduction (27 October 2026,
   * card-art-fidelity.spec.js; docs/card-rendering-regression-contract.md
   * §6), every engine's settled card art is grainy at some card sizes, and
   * each simple fix trades the grain for blur that a sharpness spec catches:
   *  - Chromium: one createImageBitmap call given a crop rectangle drops to a
   *    cheap resampler. Its canvas-2D path is clean but softens the resting
   *    hand (hand-rest-sharpness.spec.js: 0.62 of the render's detail).
   *  - Gecko: one canvas-2D draw within 8x aliases; halving to 2x first is
   *    clean but softens the enlarged card (card-sharpness.real.spec.js: 0.297
   *    of a Lanczos3 reduction's detail, floor 0.35).
   * So nothing changes until a reduction is both clean and sharp.
   *
   * Why an engine check and not a quality probe: this repo already tried to
   * settle it by probing. Commit dd7de2d5 measured `createImageBitmap` on a
   * synthetic stripe pattern, concluded it was "marginally softer" on Chromium,
   * and recorded the opposite of the truth — on real card art the ranking
   * reverses. A synthetic probe is not just expensive here, it is known to give
   * the wrong answer, so the honest gate is engine identity plus a real
   * capability check.
   */
  /* Deliberately not memoised. The answer cannot change within a real document,
     but caching it would make the engine matrix in rasterSampler.test.js
     untestable — it flips the reported identity to prove each branch — and the
     work is a regex over the UA string, cheaper than the branch that would
     guard the cache. */
  function supportsBitmapDownscale() {
    if (typeof root.createImageBitmap !== 'function') return false;
    return isChromiumEngine();
  }

  function isChromiumEngine() {
    var nav = root.navigator;
    if (!nav) return false;
    var brands = nav.userAgentData && nav.userAgentData.brands;
    if (Array.isArray(brands)) {
      return brands.some(function (entry) {
        return entry && entry.brand === 'Chromium';
      });
    }
    var ua = String(nav.userAgent || '');
    /* Chrome/Edge/Opera and Android WebView all carry "Chrome/"; Firefox never
       does, and neither does Safari — nor Chrome on iOS, which reports "CriOS"
       and IS WebKit, so excluding it here is correct rather than an oversight. */
    return /Chrome\//.test(ua) && !/Firefox|FxiOS/.test(ua);
  }

  /* A LIVE <img> CAN HAND A CANVAS NOTHING. WebKit (25 September 2026): a
     card's own on-screen <img> — complete, sized — sometimes drew into the
     card's canvas as no pixel at all, silently, while the same picture drew
     fine from a detached decoded copy; the empty canvas was settled as final
     and never redrawn (black card art, WebKit only). A draw from a connected
     <img> is therefore read back along its middle row (card art, portraits
     and backs are full-bleed there); an empty result is a fallback. A canvas
     that cannot be read (tainted) is not judged.
     Not in Chromium (28 September 2026): the read-back is a synchronous GPU
     readback there, ~100 ms per call on the 4x phone seat, and it ran on the
     first lock of every tile (1.1 s of one Collection open), for a failure
     Chromium never showed. WebKit and Gecko are still read back.
     ANY ELEMENT, ON THE PAGE OR OFF IT (round 15, 28 September 2026). The
     WebKit phone seat's first-visit warm pass settled the tutorial
     Mulligan's Elder Dragon as an empty canvas, and a drawn card in the match
     settled black in the hand. Instrumented: WebKit handed the canvas nothing
     from a card's own <img> 2-4 times in every tutorial start, and in the
     match the sampler's own detached copy of the same URL, loaded in that
     same moment, drew nothing too: WebKit keeps one decoded image per URL,
     and a copy made while the page's element is mid-decode shares that
     state. Only a connected element was read back, so the copy's empty
     sample was baked and settled. Every <img> element is read back now, the
     sampler's own copies included, and a copy is decoded before it is
     handed out (loadImage). */
  function liveDrawMayBeEmpty(image) {
    if (!image || isChromiumEngine()) return false;
    return image.isConnected === true || image.tagName === 'IMG';
  }

  function drewNothing(canvas) {
    try {
      var context = canvas.getContext('2d');
      if (!context || typeof context.getImageData !== 'function') return false;
      var width = canvas.width;
      var height = canvas.height;
      if (!(width > 0 && height > 0)) return true;
      var row = context.getImageData(0, Math.floor(height / 2), width, 1).data;
      for (var i = 3; i < row.length; i += 4) {
        if (row[i] !== 0) return false;
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  function absoluteUrl(url) {
    try {
      var base = (root.document && root.document.baseURI)
        || (root.location && root.location.href)
        || undefined;
      return new URL(url, base).href;
    } catch (_) {
      return url;
    }
  }

  /* AN ELEMENT IS THE AUTHORITY ONLY WHILE IT SHOWS THAT PICTURE (round 15,
     28 September 2026). The decoded store can hold a caller's own <img>: a
     card's before it became a canvas, a picker tile, the lobby disc. The disc
     handed over its <img> and then wrote its ~65 px raster into it, so a later
     sample that found it here would have drawn that small raster as the
     picture, and an element given a new src is loading something else. An
     element whose src no longer names the URL, or that is loading, is no
     authority for it. An object that names no source at all (a decoded copy
     handed in by a caller's own loader in a test double) is taken on trust. */
  function showsPicture(image, url) {
    if (!image || image.complete === false) return false;
    // The attribute is what the element was last told to show: it changes in
    // the same task as the write, where currentSrc still names the old
    // picture until the new one is requested.
    var named = typeof image.getAttribute === 'function'
      ? image.getAttribute('src')
      : (typeof image.src === 'string' ? image.src : null);
    if (named === null || named === '') {
      var current = typeof image.currentSrc === 'string' ? image.currentSrc : '';
      if (current) return current === url || current === absoluteUrl(url);
      return typeof image.getAttribute !== 'function' && named === null;
    }
    return named === url || absoluteUrl(named) === absoluteUrl(url);
  }

  function releaseCanvas(canvas) {
    if (!canvas) return;
    try {
      // Resetting both dimensions releases the backing pixel buffer promptly.
      // WebKit otherwise retains every stepped reduction until a later GC and
      // can terminate the page when several high-resolution cards settle at once.
      canvas.width = 1;
      canvas.height = 1;
    } catch (_) {
      // A custom/test canvas may expose immutable dimensions. Sampling has
      // already completed, so disposal remains a best-effort cleanup boundary.
    }
  }

  function isFullSource(plan, width, height) {
    return plan.source.x === 0
      && plan.source.y === 0
      && plan.source.width === width
      && plan.source.height === height;
  }

  /* `pixels`, when given, is the same picture already decoded (see
     decodePicture): the draws read it, the element only gives the geometry. */
  function renderLoadedImage(image, plan, createCanvas, stepsHelp, pixels) {
    var source = pixels || image;
    var current = source;
    var currentWidth = plan.source.width;
    var currentHeight = plan.source.height;
    var needsCrop = !isFullSource(plan, image.naturalWidth, image.naturalHeight);
    var transientCanvases = [];
    var retainedOutput = null;

    function createTransientCanvas(width, height) {
      var canvas = createCanvas(width, height);
      transientCanvases.push(canvas);
      return canvas;
    }

    try {
      /* How much reduction we are willing to hand the engine in ONE draw.
         Measured, not assumed. On real card art a single high-quality draw
         retains more detail at every ratio (Firefox 3.06x at 5.7x, WebKit
         1.73x, Chromium 1.36x) — but on a high-frequency pattern reduced 12x
         or more, WebKit and Firefox alias badly in one draw while the stepped
         path resolves correctly to flat grey. So step down until the remaining
         reduction is something the engine handles well, then draw once.
         Moonfall's real reductions are all around 5x (a 1280px still into a
         225px collection tile, a 736px cardback into 150px), which lands
         squarely in the single-draw win. */
      var oneDrawLimit = stepsHelp ? 2 : SINGLE_DRAW_MAX_RATIO;

      while (
        currentWidth > plan.targetWidth * oneDrawLimit
        || currentHeight > plan.targetHeight * oneDrawLimit
      ) {
        var nextWidth = Math.max(plan.targetWidth, Math.round(currentWidth / 2));
        var nextHeight = Math.max(plan.targetHeight, Math.round(currentHeight / 2));
        var step = createTransientCanvas(nextWidth, nextHeight);
        var stepContext = configureContext(step);
        var previous = current;
        if (needsCrop) {
          stepContext.drawImage(
            source,
            plan.source.x,
            plan.source.y,
            plan.source.width,
            plan.source.height,
            0,
            0,
            nextWidth,
            nextHeight,
          );
          needsCrop = false;
        } else {
          stepContext.drawImage(
            current,
            0,
            0,
            currentWidth,
            currentHeight,
            0,
            0,
            nextWidth,
            nextHeight,
          );
        }
        current = step;
        currentWidth = nextWidth;
        currentHeight = nextHeight;
        if (previous !== source) releaseCanvas(previous);
      }

      var output = createTransientCanvas(plan.targetWidth, plan.targetHeight);
      var outputContext = configureContext(output);
      if (needsCrop) {
        outputContext.drawImage(
          source,
          plan.source.x,
          plan.source.y,
          plan.source.width,
          plan.source.height,
          0,
          0,
          plan.targetWidth,
          plan.targetHeight,
        );
      } else {
        outputContext.drawImage(
          current,
          0,
          0,
          currentWidth,
          currentHeight,
          0,
          0,
          plan.targetWidth,
          plan.targetHeight,
        );
      }
      if (current !== source && current !== output) releaseCanvas(current);
      retainedOutput = output;
      return output;
    } finally {
      transientCanvases.forEach(function (canvas) {
        if (canvas !== retainedOutput) releaseCanvas(canvas);
      });
    }
  }

  /* Null when a LIVE <img> handed the canvas nothing (drewNothing): an empty
     sample is never baked into a data URL and settled as the picture. */
  function sampleLoadedImage(image, plan, createCanvas, stepsHelp) {
    var output = renderLoadedImage(image, plan, createCanvas, stepsHelp);
    try {
      if (liveDrawMayBeEmpty(image) && drewNothing(output)) return null;
      if (typeof output.toDataURL !== 'function') {
        throw new Error('Canvas export is unavailable');
      }
      return output.toDataURL('image/png');
    } finally {
      releaseCanvas(output);
    }
  }

  function create(options) {
    var config = options || {};
    var ImageCtor = config.ImageCtor || root.Image;
    var createCanvas = config.createCanvas || defaultCanvas;
    /* Whether the engine honours `imageSmoothingQuality = 'high'`. Probed once,
       lazily, and memoized — an explicit config value wins so the behaviour can
       be pinned in tests without a real canvas. See honoursHighQualitySmoothing
       for why this decides between one draw and a stepped reduction. */
    var highQualityDownscale = typeof config.highQualityDownscale === 'boolean'
      ? config.highQualityDownscale
      : null;
    /* Whether `createImageBitmap` outperforms canvas-2D here. Explicit config
       wins so tests can pin it without a real engine. */
    var bitmapQuality = typeof config.bitmapDownscale === 'boolean'
      ? config.bitmapDownscale
      : null;
    function bitmapDownscale() {
      if (bitmapQuality === null) bitmapQuality = supportsBitmapDownscale();
      return bitmapQuality;
    }
    function stepsHelp() {
      if (highQualityDownscale === null) {
        var probe = null;
        try {
          probe = createCanvas(1, 1);
          highQualityDownscale = honoursHighQualitySmoothing(
            probe && typeof probe.getContext === 'function' ? probe.getContext('2d') : null,
          );
        } catch (_) {
          highQualityDownscale = false;
        } finally {
          releaseCanvas(probe);
        }
      }
      return !highQualityDownscale;
    }
    var cacheLimit = Math.max(1, Math.floor(finitePositive(
      config.cacheLimit,
      DEFAULT_CACHE_LIMIT,
    )));
    var cacheByteLimit = finitePositive(
      config.cacheByteLimit,
      DEFAULT_CACHE_BYTE_LIMIT,
    );
    var loadTimeoutMs = finitePositive(
      config.loadTimeoutMs,
      DEFAULT_LOAD_TIMEOUT_MS,
    );
    var getDpr = typeof config.getDpr === 'function'
      ? config.getDpr
      : function () { return root.devicePixelRatio || 1; };
    var cache = new Map();
    var cacheWeights = new Map();
    var cacheBytes = 0;
    var decodedSources = new Map();
    var decodedSourceWeights = new Map();
    var decodedSourcePixels = 0;
    var activeImageLoads = new Map();
    var decodedSourceLimit = Math.max(1, Math.floor(finitePositive(
      config.decodedSourceLimit,
      DEFAULT_DECODED_SOURCE_LIMIT,
    )));
    var decodedPixelLimit = finitePositive(
      config.decodedPixelLimit,
      DEFAULT_DECODED_PIXEL_LIMIT,
    );
    var targetGenerations = new WeakMap();
    var generation = 0;

    function touch(key, task) {
      cache.delete(key);
      cache.set(key, task);
    }

    function forget(key) {
      cacheBytes -= cacheWeights.get(key) || 0;
      cacheWeights.delete(key);
      cache.delete(key);
    }

    function trimCache() {
      while (cache.size > cacheLimit || cacheBytes > cacheByteLimit) {
        forget(cache.keys().next().value);
      }
    }

    function remember(key, task) {
      touch(key, task);
      trimCache();
    }

    function forgetDecoded(url) {
      decodedSourcePixels -= decodedSourceWeights.get(url) || 0;
      decodedSourceWeights.delete(url);
      decodedSources.delete(url);
    }

    function touchDecoded(url, image) {
      var value = image || decodedSources.get(url);
      if (!value) return null;
      if (!showsPicture(value, url)) {
        // It shows something else now (see showsPicture): no authority.
        if (decodedSources.get(url) === value) forgetDecoded(url);
        return null;
      }
      decodedSources.delete(url);
      decodedSources.set(url, value);
      return value;
    }

    function trimDecodedSources() {
      while (
        decodedSources.size > decodedSourceLimit
        || decodedSourcePixels > decodedPixelLimit
      ) {
        forgetDecoded(decodedSources.keys().next().value);
      }
    }

    function rememberDecoded(url, image) {
      if (!url || !image) return;
      if (decodedSources.has(url)) forgetDecoded(url);
      var pixels = finitePositive(image.naturalWidth, 0)
        * finitePositive(image.naturalHeight, 0);
      if (!pixels) return;
      decodedSources.set(url, image);
      decodedSourceWeights.set(url, pixels);
      decodedSourcePixels += pixels;
      trimDecodedSources();
    }

    function rememberDecodedImage(url, image) {
      // An <img> still downloading knows its size from its header, but it is
      // not an authority yet: WebKit draws nothing from an unfinished WebP.
      if (typeof url !== 'string' || !url
        || !image
        || image.complete === false
        || !finitePositive(image.naturalWidth, 0)
        || !finitePositive(image.naturalHeight, 0)
        || !showsPicture(image, url)) {
        return false;
      }
      rememberDecoded(url, image);
      return decodedSources.get(url) === image;
    }

    function hasDecodedImage(url) {
      return typeof url === 'string' && Boolean(touchDecoded(url));
    }

    function getDecodedImage(url) {
      if (typeof url !== 'string' || !url) return null;
      return touchDecoded(url);
    }

    function loadImage(url) {
      var decoded = touchDecoded(url);
      if (decoded) return Promise.resolve(decoded);
      var active = activeImageLoads.get(url);
      if (active) return active.promise;

      var resolveTask;
      var task = new Promise(function (resolve) { resolveTask = resolve; });
      var image = null;
      var timer = null;
      var record = {
        promise: task,
        settled: false,
        cancel: function () { finish(null); },
      };

      function cancelSource() {
        if (!image) return;
        image.onload = null;
        image.onerror = null;
        if (typeof image.removeAttribute === 'function') image.removeAttribute('src');
        else image.src = '';
      }

      function finish(value) {
        if (record.settled) return;
        record.settled = true;
        if (timer !== null) clearTimeout(timer);
        timer = null;
        if (image) {
          image.onload = null;
          image.onerror = null;
          if (!value) cancelSource();
        }
        resolveTask(value);
      }

      // Install ownership before assigning src. A custom/synchronous Image
      // implementation must not complete before this request can be cancelled
      // or deduplicated.
      activeImageLoads.set(url, record);
      try {
        if (typeof ImageCtor !== 'function') {
          finish(null);
        } else {
          image = new ImageCtor();
          image.decoding = 'async';
          image.onload = function () {
            if (record.settled) return;
            if (!finitePositive(image.naturalWidth, 0) || !finitePositive(image.naturalHeight, 0)) {
              finish(null);
              return;
            }
            var ready = function () {
              if (record.settled) return;
              rememberDecoded(url, image);
              finish(image);
            };
            /* Decoded before it is handed out, where an engine can draw
               nothing from a picture still decoding (see liveDrawMayBeEmpty).
               A rejected decode still has drawable pixels (a later draw is
               read back); the load's own timeout caps the wait. */
            if (!isChromiumEngine() && typeof image.decode === 'function') {
              var decoding;
              try {
                decoding = Promise.resolve(image.decode());
              } catch (_) {
                decoding = Promise.resolve();
              }
              decoding.then(ready, ready);
              return;
            }
            ready();
          };
          image.onerror = function () { finish(null); };
          timer = setTimeout(function () { finish(null); }, loadTimeoutMs);
          image.crossOrigin = LoadScheduler.requestMode(url).crossOrigin;
          image.src = url;
        }
      } catch (_) {
        finish(null);
      }
      task.then(function () {
        if (activeImageLoads.get(url) === record) activeImageLoads.delete(url);
      }, function () {
        if (activeImageLoads.get(url) === record) activeImageLoads.delete(url);
      });
      return task;
    }

    function prepareDecodedImage(url) {
      if (typeof url !== 'string' || !url) return Promise.resolve(false);
      return loadImage(url).then(function (image) {
        return Boolean(image);
      }, function () {
        return false;
      });
    }

    function sample(url, rawRequest) {
      if (typeof url !== 'string' || !url || url.indexOf('data:') === 0) {
        return Promise.resolve({ url: url || '', sampled: false, fallback: false });
      }
      var request = normaliseRequest(rawRequest, getDpr);
      if (!request.displayWidth || !request.displayHeight) {
        return Promise.resolve({ url: url, sampled: false, fallback: true });
      }
      var key = requestKey(url, request);
      var cached = cache.get(key);
      if (cached) {
        touch(key, cached);
        touchDecoded(url);
        return cached;
      }

      var sampleFrom = function (image) {
        if (!image) return { url: url, sampled: false, fallback: true };
        try {
          var plan = computeRasterPlan({
            sourceWidth: image.naturalWidth,
            sourceHeight: image.naturalHeight,
            displayWidth: request.displayWidth,
            displayHeight: request.displayHeight,
            fit: request.fit,
            positionX: request.positionX,
            positionY: request.positionY,
            dpr: request.dpr,
            oversample: request.oversample,
          });
          if (plan.direct) return { url: url, sampled: false, fallback: false };
          var sampledUrl = sampleLoadedImage(image, plan, createCanvas, stepsHelp());
          return sampledUrl === null
            ? null
            : { url: sampledUrl, sampled: true, fallback: false };
        } catch (_) {
          return { url: url, sampled: false, fallback: true };
        }
      };
      /* A LIVE <img> THAT DREW NOTHING IS NOT THE AUTHORITY (WebKit, round
         14, 26 September 2026: 1-2 Collection tiles per page settled solid
         black). The decoded store can hold a card's own on-screen <img>
         (remembered by a lock, or by a caller that decoded it); WebKit
         sometimes hands a canvas nothing from such an element while any
         detached copy of the same URL draws. Forget it, and sample a
         detached copy — its bytes are the ones the element just loaded. */
      var task = loadImage(url).then(function (image) {
        var result = sampleFrom(image);
        if (result !== null) return result;
        if (decodedSources.get(url) === image) forgetDecoded(url);
        return loadImage(url).then(function (copy) {
          if (copy === image) return { url: url, sampled: false, fallback: true };
          return sampleFrom(copy) || { url: url, sampled: false, fallback: true };
        }, function () {
          return { url: url, sampled: false, fallback: true };
        });
      }, function () {
        return { url: url, sampled: false, fallback: true };
      });

      remember(key, task);
      task.then(function (result) {
        if (cache.get(key) !== task) return;
        if (result.fallback) {
          forget(key);
          return;
        }
        if (result.sampled && typeof result.url === 'string') {
          var bytes = result.url.length * 2;
          cacheWeights.set(key, bytes);
          cacheBytes += bytes;
          trimCache();
        }
      });
      return task;
    }

    function renderDecodedImage(image, rawRequest) {
      return renderDecodedPixels(image, rawRequest, null);
    }

    /* `onBlank` is told when a LIVE element handed the canvas nothing — the
       one fallback that says the element is no authority (renderCachedImage).
       The others (no display size, no plan, no canvas) say nothing about it. */
    function renderDecodedPixels(image, rawRequest, onBlank) {
      if (!image
        || !finitePositive(image.naturalWidth, 0)
        || !finitePositive(image.naturalHeight, 0)) {
        return { canvas: null, sampled: false, fallback: true };
      }
      var request = normaliseRequest(rawRequest, getDpr);
      if (!request.displayWidth || !request.displayHeight) {
        return { canvas: null, sampled: false, fallback: true };
      }
      try {
        var plan = computeRasterPlan({
          sourceWidth: image.naturalWidth,
          sourceHeight: image.naturalHeight,
          displayWidth: request.displayWidth,
          displayHeight: request.displayHeight,
          fit: request.fit,
          positionX: request.positionX,
          positionY: request.positionY,
          dpr: request.dpr,
          oversample: request.oversample,
        });
        if (plan.direct && rawRequest?.forceCanvas !== true) {
          return { canvas: null, sampled: false, fallback: false };
        }
        var pixels = decodedPixelsOf(image, rawRequest);
        var canvas = renderLoadedImage(image, plan, createCanvas, stepsHelp(), pixels);
        if (!pixels && liveDrawMayBeEmpty(image) && drewNothing(canvas)) {
          releaseCanvas(canvas);
          if (onBlank) onBlank();
          return { canvas: null, sampled: false, fallback: true };
        }
        if (plan.direct) {
          // `forceCanvas` is a continuity primitive, not a claim that new
          // detail was synthesized. The authority pixels are copied into a
          // ready bitmap for first paint, but remain truthfully direct when
          // the source is smaller than the requested physical-pixel reserve.
          return {
            canvas: canvas,
            sampled: false,
            materialized: true,
            fallback: false,
          };
        }
        return { canvas: canvas, sampled: true, fallback: false };
      } catch (_) {
        return { canvas: null, sampled: false, fallback: true };
      }
    }

    /* The picture decoded off the main thread (decodePicture) stands in for
       the element in a draw: the same picture, so the same size. */
    function decodedPixelsOf(image, rawRequest) {
      var pixels = rawRequest && rawRequest.pixels;
      return pixels
        && pixels.width === image.naturalWidth
        && pixels.height === image.naturalHeight
        ? pixels
        : null;
    }

    function renderCachedImage(url, rawRequest) {
      var image = touchDecoded(url);
      if (!image) return { canvas: null, sampled: false, fallback: true };
      var drewBlank = false;
      var rendered = renderDecodedPixels(image, rawRequest, function () { drewBlank = true; });
      // A live <img> that drew nothing is no authority (see sample()): the
      // next sample of this URL loads a detached copy instead. Any other
      // fallback leaves it where it is.
      if (drewBlank && decodedSources.get(url) === image) forgetDecoded(url);
      return rendered;
    }

    /**
     * Same contract as `renderDecodedImage`, but performs the reduction with
     * `createImageBitmap(..., { resizeQuality: 'high' })` where that engine
     * downscales better than canvas-2D (see `supportsBitmapDownscale`).
     *
     * The bitmap is blitted 1:1 into an ordinary `<canvas>`, so every consumer
     * keeps a real DOM canvas it can restyle, stamp datasets on, read pixels
     * back out of, and release by zeroing its dimensions. The bitmap itself is
     * closed immediately — it is a scratch buffer, not a retained cache, so
     * this adds no resident pixels beyond the canvas that was already there.
     *
     * Resolves to the identical shape on every path, including the fallback,
     * so callers need no branch of their own.
     */
    /*
     * ONE DECODE PER PICTURE, OFF THE MAIN THREAD (round 15, 28 September
     * 2026, Chrome phone seat, 4x CPU, real 1280x1589 card art):
     *   createImageBitmap(<img>, crop, resize high)     165-188 ms, EVERY call
     *   createImageBitmap(<img>)  (the decode alone)     164-177 ms
     *   createImageBitmap(decoded bitmap, crop, resize)    4-9 ms
     *   fetch from the HTTP cache + createImageBitmap(blob): 0-1 ms of main
     *     thread, decoded off it in 42-47 ms, no long task
     * and a reduction read from the decoded bitmap is the SAME pixels (0 of
     * 267,336 bytes differed, on two cards). The bitmap tier's cost was the
     * picture's full decode, redone on the main thread for every size of
     * every card (2.7 s of one phone Collection open). So a reduction reads
     * the picture's bytes back from the browser's cache — requested exactly
     * as the picture itself was (same mode, same credentials), so the cache
     * answers them — has them decoded off the main thread, and reads that
     * decoded bitmap; every other reduction of the picture shares it while it
     * is held. It is never a download: a same-origin picture is read only if
     * cached, a cross-origin one only when it was itself requested with CORS
     * (its cached copy is a CORS one; `Vary: Origin` would miss otherwise),
     * and a read the page could have heard back within CACHED_BYTES_CAP_MS
     * and did not is abandoned (a cache hit takes 1-8 ms; never more than
     * CACHED_BYTES_HARD_CAP_MS in all). Any miss falls back to the <img>, as
     * before. At most FULL_DECODE_SLOTS decoded pictures (~8 MB each) are held,
     * each let go FULL_DECODE_IDLE_MS after its last use. Chromium only (the
     * bitmap tier). Contract: tests/unit/utils/rasterSampler.currentWork.test.js.
     * Six, not three, since a card's first raster reads its decode too
     * (decodePicture, 27 October 2026): with three, a deck builder page of
     * eight waited past FULL_DECODE_SLOT_WAIT_MS at 6x CPU, and three of its
     * cards decoded on the main thread after all.
     */
    var CACHED_BYTES_CAP_MS = 150;
    var CACHED_BYTES_LATE_MS = 30;
    var CACHED_BYTES_HARD_CAP_MS = 1000;
    var FULL_DECODE_SLOTS = 6;
    var FULL_DECODE_IDLE_MS = 3000;
    var FULL_DECODE_SLOT_WAIT_MS = 1500;
    var fullDecodes = new Map();
    var fullDecodeSlotWaiters = [];

    function pictureUrl(image) {
      if (!image || typeof image !== 'object') return '';
      var named = typeof image.getAttribute === 'function' ? image.getAttribute('src') : '';
      var url = image.currentSrc || named || (typeof image.src === 'string' ? image.src : '');
      if (typeof url !== 'string' || !url) return '';
      var absolute = absoluteUrl(url);
      return /^https?:/i.test(absolute) ? absolute : '';
    }

    /* The request that finds the picture's own cached copy, or null. */
    function cachedBytesInit(url, image) {
      var pageOrigin = root.location && root.location.origin;
      var sameOrigin = false;
      try {
        sameOrigin = Boolean(pageOrigin) && new URL(url).origin === pageOrigin;
      } catch (_) {
        return null;
      }
      var mode = image.crossOrigin;
      var credentials = mode === 'use-credentials' ? 'include' : 'same-origin';
      if (sameOrigin) return { mode: 'same-origin', credentials: credentials, cache: 'only-if-cached' };
      if (mode !== 'anonymous' && mode !== '' && mode !== 'use-credentials') return null;
      return { mode: 'cors', credentials: credentials, cache: 'force-cache' };
    }

    function nowMs() {
      return root.performance && typeof root.performance.now === 'function'
        ? root.performance.now()
        : Date.now();
    }

    function readCachedBytes(url, init) {
      if (typeof root.fetch !== 'function' || typeof root.AbortController !== 'function') {
        return Promise.resolve(null);
      }
      var controller = new root.AbortController();
      var startedAt = nowMs();
      var timer = null;
      /* The cap counts time the page could have heard back. A cache hit is
         answered in 1-8 ms, but its answer is a task queued behind whatever
         long task the main thread is running (a page build, another card's
         reduction): a timer that fires late is given one more turn after
         the tasks already queued, never more than the hard cap in all. */
      var arm = function (delay) {
        var armedAt = nowMs();
        timer = setTimeout(function () {
          var late = nowMs() - armedAt - delay;
          if (late > CACHED_BYTES_LATE_MS && nowMs() - startedAt < CACHED_BYTES_HARD_CAP_MS) {
            arm(0);
            return;
          }
          controller.abort();
        }, delay);
      };
      arm(CACHED_BYTES_CAP_MS);
      var done = function (value) {
        clearTimeout(timer);
        return value;
      };
      return Promise.resolve()
        .then(function () {
          return root.fetch(url, Object.assign({}, init, { signal: controller.signal }));
        })
        .then(function (response) {
          return response && response.ok && typeof response.blob === 'function' ? response.blob() : null;
        })
        .then(function (blob) {
          return done(blob && blob.size > 0 ? blob : null);
        }, function () {
          return done(null);
        });
    }

    function closeFullDecode(url, entry) {
      if (entry.idleTimer !== null) clearTimeout(entry.idleTimer);
      entry.idleTimer = null;
      if (fullDecodes.get(url) === entry) fullDecodes.delete(url);
      if (entry.bitmap && typeof entry.bitmap.close === 'function') entry.bitmap.close();
      entry.bitmap = null;
    }

    function freeFullDecodeSlot() {
      if (fullDecodes.size < FULL_DECODE_SLOTS) return true;
      var idle = null;
      fullDecodes.forEach(function (entry, url) {
        if (!idle && entry.users === 0 && entry.bitmap) idle = [url, entry];
      });
      if (!idle) return false;
      closeFullDecode(idle[0], idle[1]);
      return true;
    }

    /* Each waiter takes its slot in the same call that wakes it (starting
       its decode, or joining one started meanwhile), so a slot freed once is
       never promised twice. */
    function wakeFullDecodeWaiters() {
      while (fullDecodeSlotWaiters.length && freeFullDecodeSlot()) {
        fullDecodeSlotWaiters.shift()(true);
      }
    }

    function leaseOf(url, entry) {
      entry.users += 1;
      if (entry.idleTimer !== null) clearTimeout(entry.idleTimer);
      entry.idleTimer = null;
      var released = false;
      return entry.promise.then(function (bitmap) {
        var release = function () {
          if (released) return;
          released = true;
          entry.users -= 1;
          if (entry.users > 0 || fullDecodes.get(url) !== entry) return;
          if (!entry.bitmap) {
            fullDecodes.delete(url);
            wakeFullDecodeWaiters();
            return;
          }
          entry.idleTimer = setTimeout(function () {
            closeFullDecode(url, entry);
            wakeFullDecodeWaiters();
          }, FULL_DECODE_IDLE_MS);
          // Idle, it is held only until a picture waiting for a slot needs it.
          wakeFullDecodeWaiters();
        };
        if (!bitmap) {
          release();
          return null;
        }
        return { bitmap: bitmap, release: release };
      });
    }

    /* Starts the decode in a slot the caller has just found free. */
    function startFullDecode(url, init, width, height) {
      var entry = { users: 0, bitmap: null, idleTimer: null, promise: null };
      fullDecodes.set(url, entry);
      entry.promise = readCachedBytes(url, init).then(function (blob) {
        return blob ? root.createImageBitmap(blob) : null;
      }).then(function (bitmap) {
        if (!bitmap) return null;
        // The picture itself, or nothing: another size or shape (an
        // orientation, another file) is never read as this picture.
        if (bitmap.width !== width || bitmap.height !== height || fullDecodes.get(url) !== entry) {
          if (typeof bitmap.close === 'function') bitmap.close();
          return null;
        }
        entry.bitmap = bitmap;
        return bitmap;
      }, function () {
        return null;
      });
      return entry;
    }

    /* What a lease of this picture would read, or null (no URL to read back,
       no CORS copy in the cache, no fetch): the reduction reads the <img>. */
    function pictureLeaseRequest(image) {
      if (!bitmapDownscale()
        || typeof root.createImageBitmap !== 'function'
        || typeof root.fetch !== 'function'
        || typeof root.AbortController !== 'function') {
        return null;
      }
      var url = pictureUrl(image);
      var init = url ? cachedBytesInit(url, image) : null;
      return init ? { url: url, init: init, width: image.naturalWidth, height: image.naturalHeight } : null;
    }

    /* The picture decoded off the main thread, as { bitmap, release }, or null. */
    function leaseDecodedPicture(leaseRequest) {
      var url = leaseRequest.url;
      var init = leaseRequest.init;
      var width = leaseRequest.width;
      var height = leaseRequest.height;
      var existing = fullDecodes.get(url);
      if (existing) return leaseOf(url, existing);
      if (freeFullDecodeSlot()) return leaseOf(url, startFullDecode(url, init, width, height));
      // Every slot holds a picture in use: wait for one, capped; past the
      // cap the reduction reads the <img> as before.
      return new Promise(function (resolve) {
        var settled = false;
        var timer = null;
        var take = function (granted) {
          if (settled) return;
          settled = true;
          if (timer !== null) clearTimeout(timer);
          var at = fullDecodeSlotWaiters.indexOf(take);
          if (at >= 0) fullDecodeSlotWaiters.splice(at, 1);
          var joined = fullDecodes.get(url);
          if (joined) {
            resolve(leaseOf(url, joined));
            if (granted) wakeFullDecodeWaiters();
            return;
          }
          resolve(granted ? leaseOf(url, startFullDecode(url, init, width, height)) : null);
        };
        timer = setTimeout(function () { take(false); }, FULL_DECODE_SLOT_WAIT_MS);
        fullDecodeSlotWaiters.push(take);
      });
    }

    function renderDecodedImageHighQuality(image, rawRequest, options) {
      /* Each step of the reduction does real main-thread work. A caller that
         settles many cards at once supplies `schedule(work)` (resolving to
         work's result) to decide when each step runs, so a page of cards is
         not reduced inside one long task. Without it, steps run as before. */
      var schedule = options && typeof options.schedule === 'function' ? options.schedule : null;
      var inStep = function (work) { return schedule ? schedule(work) : work(); };
      /* A STEP NOBODY WANTS IS NEVER RUN (round 15, 28 September 2026). A
         caller that slices the steps into later tasks supplies `isCurrent()`:
         asked as each step starts, false once the card was resized again,
         left the page or lost its layout box. Steps queued for a size the
         card no longer had ran to the end and were thrown away: 41 calls
         (6.1 s) after one phone resize, and 18-35 calls (2.7-5 s) after the
         Collection had closed. A dropped reduction resolves `stale` with no
         canvas; it never falls back to a canvas-2D raster nobody asked for. */
      var isCurrent = options && typeof options.isCurrent === 'function' ? options.isCurrent : null;
      var STALE = {};
      var wanted = function () {
        if (!isCurrent) return true;
        try {
          return isCurrent() !== false;
        } catch (_) {
          return false;
        }
      };
      var staleResult = function () {
        return { canvas: null, sampled: false, fallback: false, stale: true };
      };
      var sync = function () {
        var rendered = renderDecodedImage(image, rawRequest);
        // `canvas2d` is a terminal outcome for this exact request, not an
        // invitation to retry createImageBitmap forever. Callers can still
        // retry after a source or geometry signature changes.
        if (rendered && rendered.canvas) rendered.tier = 'canvas2d';
        return Promise.resolve(rendered);
      };
      if (!bitmapDownscale()) return sync();
      if (!image
        || !finitePositive(image.naturalWidth, 0)
        || !finitePositive(image.naturalHeight, 0)) {
        return sync();
      }
      var request = normaliseRequest(rawRequest, getDpr);
      if (!request.displayWidth || !request.displayHeight) return sync();
      var plan;
      try {
        plan = computeRasterPlan({
          sourceWidth: image.naturalWidth,
          sourceHeight: image.naturalHeight,
          displayWidth: request.displayWidth,
          displayHeight: request.displayHeight,
          fit: request.fit,
          positionX: request.positionX,
          positionY: request.positionY,
          dpr: request.dpr,
          oversample: request.oversample,
        });
      } catch (_) {
        return sync();
      }
      // A direct plan is not a reduction at all; there is nothing to improve.
      if (plan.direct) return sync();
      /* THE SAME RATIO RULE THE CANVAS-2D SIBLING ALREADY OBEYS.
         `renderLoadedImage` steps down once the remaining reduction passes
         SINGLE_DRAW_MAX_RATIO, because no engine's one-shot filter has enough
         taps beyond that and the result aliases — manufactured edges, not
         detail. This branch skipped that decision entirely and handed
         `createImageBitmap` the whole reduction in one call.
         Measured on the real 1280x1589 card into a 132px physically-sized
         collection tile (9.7x) via the card-sharpness real spec's Lanczos3
         oracle: Laplacian detail came out at 3.97x the ideal while luma
         correlation with that ideal FELL to 0.727, below the 0.858 of the
         blurry transform-stretched control. Variance rewards aliasing;
         correlation does not, and the two disagreeing that hard is the
         signature §2b of the card contract describes.
         Halving through the same high-quality bitmap resampler keeps
         Chromium's advantage and removes the aliasing. */
      var steps = [];
      var stepWidth = plan.source.width;
      var stepHeight = plan.source.height;
      while (steps.length < 8
        && (stepWidth / plan.targetWidth > SINGLE_DRAW_MAX_RATIO
          || stepHeight / plan.targetHeight > SINGLE_DRAW_MAX_RATIO)) {
        stepWidth = Math.max(plan.targetWidth, Math.round(stepWidth / 2));
        stepHeight = Math.max(plan.targetHeight, Math.round(stepHeight / 2));
        steps.push({ width: stepWidth, height: stepHeight });
      }
      // The last entry is always the real target, so a reduction within the
      // limit produces exactly the single call this branch has always made.
      steps.push({ width: plan.targetWidth, height: plan.targetHeight });
      // The picture decoded once, off the main thread (ONE DECODE PER
      // PICTURE); the first step reads it instead of the <img>, and it is
      // held only until that step has read it. It is asked for when the
      // step's turn comes, so a reduction dropped while it waited in the
      // queue never decodes anything.
      var leaseRequest = pictureLeaseRequest(image);
      var lease = null;
      var releaseLease = function () {
        if (lease) lease.release();
        lease = null;
      };
      var reduce = function (source, step, cropped) {
        var options = { resizeWidth: step.width, resizeHeight: step.height, resizeQuality: 'high' };
        // Only the FIRST call reads through the plan's cover crop; every
        // later one consumes the whole intermediate.
        return cropped
          ? root.createImageBitmap(source, plan.source.x, plan.source.y, plan.source.width, plan.source.height, options)
          : root.createImageBitmap(source, 0, 0, source.width, source.height, options);
      };
      var bitmapTask = steps.reduce(function (pending, step) {
        return pending.then(function (previous) {
          if (previous === STALE) return STALE;
          var next;
          if (previous) {
            next = inStep(function () { return wanted() ? reduce(previous, step, false) : STALE; });
          } else if (!leaseRequest) {
            next = inStep(function () { return wanted() ? reduce(image, step, true) : STALE; });
          } else {
            next = Promise.resolve(inStep(function () {
              return wanted() ? leaseDecodedPicture(leaseRequest) : STALE;
            })).then(function (leased) {
              if (leased === STALE) return STALE;
              lease = leased;
              // No decoded picture: the <img> is read as before, in a slice.
              if (!leased) {
                return inStep(function () { return wanted() ? reduce(image, step, true) : STALE; });
              }
              // From the decoded picture the step is 4-9 ms: run as it lands,
              // so its slot is free for the next picture at once.
              return wanted() ? reduce(leased.bitmap, step, true) : STALE;
            });
          }
          return Promise.resolve(next).then(function (resized) {
            if (!previous) releaseLease();
            // Intermediates are scratch buffers; only the last one survives.
            if (previous && typeof previous.close === 'function') previous.close();
            return resized;
          }, function (error) {
            releaseLease();
            throw error;
          });
        });
      }, Promise.resolve(null));
      return bitmapTask.then(function (bitmap) {
        releaseLease();
        if (bitmap === STALE) return staleResult();
        return inStep(function () {
          if (!wanted()) {
            if (bitmap && typeof bitmap.close === 'function') bitmap.close();
            return staleResult();
          }
          var canvas = null;
          try {
            canvas = createCanvas(plan.targetWidth, plan.targetHeight);
            var context = configureContext(canvas);
            context.drawImage(bitmap, 0, 0);
            return { canvas: canvas, sampled: true, fallback: false, tier: 'bitmap' };
          } catch (_) {
            releaseCanvas(canvas);
            return sync();
          } finally {
            if (bitmap && typeof bitmap.close === 'function') bitmap.close();
          }
        });
      }, function () {
        releaseLease();
        return sync();
      });
    }

    /*
     * A CARD'S FIRST DRAW READS A PICTURE ALREADY DECODED OFF THE MAIN THREAD
     * (replay 01a175f7, 26 October 2026, Windows Chrome deck builder). The
     * first canvas draw of a picture decodes it on the main thread: 150-230 ms
     * a picture at 6x CPU, 2 of the 2.6-3.2 s a deck builder page took to
     * turn. Unthrottled, 8 real card pictures (main-thread ms of the draws):
     *   Chrome   <img> 330-350; img.decode() first: no better (it decodes for
     *            the compositor, not for a canvas); cached bytes decoded by
     *            createImageBitmap(blob): 37-57
     *   Firefox  <img> 385-396; img.decode() first: 8-11
     *   WebKit   <img> 426-555; img.decode() first: 86-126
     * So the picture is decoded the way its engine decodes off the main
     * thread: on the bitmap tier its cached bytes, through the same lease a
     * reduction reads (ONE DECODE PER PICTURE); elsewhere the element itself.
     * Resolves { pixels, release } (`pixels` null when the element itself is
     * now decoded; `release` once the draw has read them), or null when there
     * is nothing to wait for. Never waited on past PICTURE_DECODE_CAP_MS: the
     * caller then draws the element, as before.
     * Contract: tests/unit/utils/rasterSampler.firstDraw.test.js.
     */
    var PICTURE_DECODE_CAP_MS = 3000;

    function decodePicture(image) {
      if (!image
        || image.complete === false
        || !finitePositive(image.naturalWidth, 0)
        || !finitePositive(image.naturalHeight, 0)) {
        return Promise.resolve(null);
      }
      var decoding = null;
      var leaseRequest = pictureLeaseRequest(image);
      if (leaseRequest) {
        decoding = leaseDecodedPicture(leaseRequest).then(function (lease) {
          return lease ? { pixels: lease.bitmap, release: lease.release } : null;
        });
      } else if (!isChromiumEngine() && typeof image.decode === 'function') {
        decoding = Promise.resolve().then(function () {
          return image.decode();
        }).then(function () {
          return { pixels: null, release: function () {} };
        });
      }
      if (!decoding) return Promise.resolve(null);
      return new Promise(function (resolve) {
        var settled = false;
        var timer = setTimeout(function () {
          settled = true;
          resolve(null);
        }, PICTURE_DECODE_CAP_MS);
        decoding.then(function (decoded) {
          if (settled) {
            if (decoded) decoded.release();
            return;
          }
          settled = true;
          clearTimeout(timer);
          resolve(decoded);
        }, function () {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(null);
        });
      });
    }

    function directSource(target) {
      if (target && typeof target.getAttribute === 'function') {
        return target.getAttribute('src') || '';
      }
      return target && typeof target.src === 'string' ? target.src : '';
    }

    function writeSource(target, url) {
      if (directSource(target) === url) return;
      if (target) target.crossOrigin = LoadScheduler.requestMode(url).crossOrigin;
      if (target && typeof target.setAttribute === 'function') {
        target.setAttribute('src', url);
      } else if (target) {
        target.src = url;
      }
    }

    function readElementRequest(target, rawRequest) {
      var input = Object.assign({}, rawRequest || {});
      var rect = null;
      if ((!input.displayWidth || !input.displayHeight)
        && target
        && typeof target.getBoundingClientRect === 'function') {
        rect = target.getBoundingClientRect();
      }
      input.displayWidth = finitePositive(
        input.displayWidth,
        finitePositive(rect && rect.width, finitePositive(target && target.clientWidth, 0)),
      );
      input.displayHeight = finitePositive(
        input.displayHeight,
        finitePositive(rect && rect.height, finitePositive(target && target.clientHeight, 0)),
      );

      if ((!input.fit || input.positionX == null || input.positionY == null)
        && target
        && typeof root.getComputedStyle === 'function') {
        try {
          var style = root.getComputedStyle(target);
          if (!input.fit) input.fit = style.objectFit;
          var parts = String(style.objectPosition || '').trim().split(/\s+/);
          if (input.positionX == null) input.positionX = parts[0];
          if (input.positionY == null) input.positionY = parts[1];
        } catch (_) {
          // Detached test doubles and pre-layout elements use explicit options.
        }
      }
      return input;
    }

    function enhanceImage(target, url, rawRequest) {
      if (!target || (typeof target !== 'object' && typeof target !== 'function')) {
        return Promise.resolve({
          url: url || '',
          sampled: false,
          fallback: true,
          applied: false,
          stale: false,
        });
      }
      generation += 1;
      var requestGeneration = generation;
      targetGenerations.set(target, requestGeneration);
      // Callers normally paint the authoritative direct URL before asking for
      // a higher-quality sample. Keep an already-rendered sample visible while
      // re-sampling the same source at a larger hover/viewport size; replacing
      // it with the direct source here would create a visible one-frame jump.
      var startingPaint = directSource(target);
      if (!startingPaint) {
        writeSource(target, url);
        startingPaint = url;
      }
      var request = readElementRequest(target, rawRequest);
      var applyGuard = rawRequest && typeof rawRequest.applyGuard === 'function'
        ? rawRequest.applyGuard
        : null;

      return sample(url, request).then(function (result) {
        var blocked = applyGuard && !applyGuard();
        var stale = targetGenerations.get(target) !== requestGeneration
          || directSource(target) !== startingPaint
          || blocked;
        if (stale) {
          return Object.assign({}, result, {
            applied: false,
            stale: true,
            blocked: Boolean(blocked),
          });
        }
        if (result.fallback && startingPaint.indexOf('data:') === 0) {
          return Object.assign({}, result, {
            applied: false,
            stale: false,
            retained: true,
          });
        }
        writeSource(target, result.url);
        return Object.assign({}, result, { applied: true, stale: false });
      });
    }

    return {
      sample: sample,
      rememberDecodedImage: rememberDecodedImage,
      hasDecodedImage: hasDecodedImage,
      getDecodedImage: getDecodedImage,
      prepareDecodedImage: prepareDecodedImage,
      decodePicture: decodePicture,
      renderDecodedImage: renderDecodedImage,
      renderDecodedImageHighQuality: renderDecodedImageHighQuality,
      renderCachedImage: renderCachedImage,
      enhanceImage: enhanceImage,
      cacheSize: function () { return cache.size; },
      clear: function () {
        cache.clear();
        cacheWeights.clear();
        cacheBytes = 0;
        decodedSources.clear();
        decodedSourceWeights.clear();
        decodedSourcePixels = 0;
        Array.from(activeImageLoads.values()).forEach(function (record) {
          if (record && typeof record.cancel === 'function') record.cancel();
        });
        activeImageLoads.clear();
        Array.from(fullDecodes.entries()).forEach(function (pair) {
          if (pair[1].users === 0) closeFullDecode(pair[0], pair[1]);
        });
      },
    };
  }

  var singleton = create();
  /* Shared so the other stepped downscalers (cardback raster, indicator icons)
     make the SAME decision from the SAME probe instead of each re-deriving it —
     they lose detail in exactly the same way, on the same engines. */
  var sharedHighQuality = null;
  function supportsHighQualityDownscale(makeCanvas) {
    if (sharedHighQuality !== null) return sharedHighQuality;
    var probe = null;
    try {
      probe = (makeCanvas || defaultCanvas)(1, 1);
      sharedHighQuality = honoursHighQualitySmoothing(
        probe && typeof probe.getContext === 'function' ? probe.getContext('2d') : null,
      );
    } catch (_) {
      sharedHighQuality = false;
    } finally {
      releaseCanvas(probe);
    }
    return sharedHighQuality;
  }

  return {
    computeRasterPlan: computeRasterPlan,
    supportsHighQualityDownscale: supportsHighQualityDownscale,
    supportsBitmapDownscale: supportsBitmapDownscale,
    create: create,
    sample: singleton.sample,
    rememberDecodedImage: singleton.rememberDecodedImage,
    hasDecodedImage: singleton.hasDecodedImage,
    getDecodedImage: singleton.getDecodedImage,
    prepareDecodedImage: singleton.prepareDecodedImage,
    decodePicture: singleton.decodePicture,
    renderDecodedImage: singleton.renderDecodedImage,
    renderDecodedImageHighQuality: singleton.renderDecodedImageHighQuality,
    renderCachedImage: singleton.renderCachedImage,
    enhanceImage: singleton.enhanceImage,
    cacheSize: singleton.cacheSize,
    clear: singleton.clear,
  };
});
